/* eslint-disable react/prop-types */
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
    Box, Typography, TextField, MenuItem, Divider, Stack, Chip, Alert,
} from '@mui/material';
import Iconify from '../Iconify';
import DateOfBirthField from '../fields/DateOfBirthField';
import useDentallyPatientLink from '../../hooks/useDentallyPatientLink';
import { useSelector } from '../../redux/store';
import { callApi } from '../../utils/apiCall';
import { dobFromApi } from '../../utils/dobDate';
import { CANONICAL_SALUTATION_VALUES, getSalutationOptions } from '../../utils/getReferralData';
import { sectionCardStyle, sectionLabelStyle } from '../../styles/DentallyBookingStyle';

const FALLBACK_SALUTATION_OPTIONS = getSalutationOptions().filter(option => option.value);
const CANONICAL_SALUTATIONS = new Set(CANONICAL_SALUTATION_VALUES);

// The authoritative profile's dob arrives in the API's day-first format and is
// converted to ISO by dobFromApi before use. This only has to answer "did it
// convert", not re-validate the calendar: dobFromApi returns '' when it could
// not parse. A lead with NO dob is legitimate and must still pass.
//
// Previously this tested the RAW API value against /^\d{4}-\d{2}-\d{2}$/, which
// our API never emits, so the check failed for every lead that HAD a date of
// birth and sent the whole step into its error state.
const authoritativeDobIsUsable = (raw, converted) => raw == null || converted !== '';

/**
 * Wizard step: Patient Details
 *
 * Sits between Treatment & Availability and Confirm Booking.
 * Shows the CRM lead's name/contact as read-only and lets the user
 * fill in the supported Dentally-required fields that are missing (salutation, DOB).
 * Fields that already exist are pre-populated and read-only in this collect-only step.
 *
 * Props:
 *   existingLead  — CRM lead object (id, first_name, last_name, salutation, dob, lead_contact)
 *   onPatientDataCollected — callback(data) called with supported patient data when valid
 */
const DentallyPatientDetailsWizardStep = forwardRef(({
    practiceId,
    existingLead,
    onPatientDataCollected,
    onStepStateChange,
}, ref) => {
    const schemaSalutationOptions = useSelector(state => state.schema?.salutationOptions);
    const salutationOptions = Array.isArray(schemaSalutationOptions)
        ? schemaSalutationOptions.filter(option => CANONICAL_SALUTATIONS.has(option?.value))
        : [];
    const availableSalutationOptions = salutationOptions.length === FALLBACK_SALUTATION_OPTIONS.length
        ? salutationOptions
        : FALLBACK_SALUTATION_OPTIONS;
    const scopedPracticeId = practiceId
        || existingLead?.practice?.id
        || existingLead?.practice_id
        || existingLead?.practice;
    const leadId = existingLead?.id || existingLead?.lead_id;
    const profileScopeKey = `${scopedPracticeId || ''}:${leadId || ''}`;
    const currentProfileScopeKey = useRef(profileScopeKey);
    currentProfileScopeKey.current = profileScopeKey;
    const [profile, setProfile] = useState({
        scopeKey: null, status: 'loading', salutation: '', dob: '',
    });
    const [title, setTitle] = useState('');
    const [dob, setDob] = useState('');
    const [errors, setErrors] = useState({});
    const {
        link: dentallyPatientLink,
        isLoading: patientLinkLoading,
        outcome: patientLinkOutcome,
    } = useDentallyPatientLink(scopedPracticeId, leadId);
    const patientId = dentallyPatientLink?.dentally_patient_id;
    const isDurablyLinked = patientLinkOutcome === 'unique' && Boolean(patientId);
    const isExplicitlyUnlinked = patientLinkOutcome === 'none';
    const linkUnresolved = !isDurablyLinked && !isExplicitlyUnlinked;

    useEffect(() => {
        let active = true;
        setProfile({ scopeKey: profileScopeKey, status: 'loading', salutation: '', dob: '' });
        setTitle('');
        setDob('');
        setErrors({});

        if (!scopedPracticeId || !leadId) {
            setProfile({ scopeKey: profileScopeKey, status: 'error', salutation: '', dob: '' });
            return () => { active = false; };
        }

        callApi(`/api/v1/practices/${scopedPracticeId}/leads/${leadId}/?fields={id,salutation,dob}`)
            .then((response) => {
                const data = response?.data;
                const valid = response?.ok === true
                    && response?.status === 200
                    && data?.id === leadId
                    && (data.salutation === null || typeof data.salutation === 'string')
                    && authoritativeDobIsUsable(data.dob, dobFromApi(data.dob));
                if (!active) return;
                if (!valid) {
                    setProfile({ scopeKey: profileScopeKey, status: 'error', salutation: '', dob: '' });
                    return;
                }
                const authoritativeTitle = CANONICAL_SALUTATIONS.has(data.salutation)
                    ? data.salutation
                    : '';
                const authoritativeDob = dobFromApi(data.dob);
                setTitle(authoritativeTitle);
                setDob(authoritativeDob);
                setProfile({
                    scopeKey: profileScopeKey,
                    status: 'ready',
                    salutation: authoritativeTitle,
                    dob: authoritativeDob,
                });
            })
            .catch(() => {
                if (active) {
                    setProfile({ scopeKey: profileScopeKey, status: 'error', salutation: '', dob: '' });
                }
            });

        return () => { active = false; };
    }, [profileScopeKey]);

    const profileMatchesScope = profile.scopeKey === profileScopeKey;
    const profileLoading = !profileMatchesScope || profile.status === 'loading';
    const profileUnresolved = !profileMatchesScope || profile.status !== 'ready';
    const persistedSalutation = profileMatchesScope ? profile.salutation : '';
    const persistedDob = profileMatchesScope ? profile.dob : '';
    const needsTitle = !isDurablyLinked && !persistedSalutation;
    const needsDob = !isDurablyLinked && !persistedDob;
    const hasMissingFields = needsTitle || needsDob;

    useEffect(() => {
        onStepStateChange?.({
            buttonLabel: 'Next',
            buttonDisabled: linkUnresolved || profileUnresolved,
            buttonLoading: patientLinkLoading || profileLoading,
        });
    }, [linkUnresolved, onStepStateChange, patientLinkLoading, profileLoading, profileUnresolved]);

    useImperativeHandle(ref, () => ({
        submit() {
            // The absence of a link is not authoritative until the scoped lookup settles.
            if (linkUnresolved || profileUnresolved || profile.scopeKey !== currentProfileScopeKey.current) return null;
            if (isDurablyLinked) {
                const linkedData = {
                    dentallyPatientLinked: true,
                    patientLinkOutcome: 'unique',
                    salutation: title,
                    dob,
                    persistedPatientFields: {
                        salutation: Boolean(persistedSalutation),
                        dob: Boolean(persistedDob),
                    },
                };
                onPatientDataCollected?.(linkedData);
                return linkedData;
            }
            const errs = {};
            const canonicalTitle = String(title || '').toUpperCase();
            if (needsTitle && !CANONICAL_SALUTATIONS.has(canonicalTitle)) errs.title = 'Required';
            if (needsDob && !dob) errs.dob = 'Required';
            if (Object.keys(errs).length > 0) {
                setErrors(errs);
                return null;
            }
            const data = {
                dentallyPatientLinked: false,
                patientLinkOutcome: 'none',
                salutation: canonicalTitle,
                dob,
                persistedPatientFields: {
                    salutation: Boolean(persistedSalutation),
                    dob: Boolean(persistedDob),
                },
            };
            onPatientDataCollected?.(data);
            return data;
        },
    }));

    const fullName = [existingLead?.first_name, existingLead?.last_name].filter(Boolean).join(' ') || 'Unknown Patient';
    const phone = existingLead?.lead_contact?.phone;
    const email = existingLead?.lead_contact?.email;

    let patientStatusChip;
    if (patientLinkLoading) {
        patientStatusChip = (
            <Chip
                icon={<Iconify icon="mdi:loading" width={14} />}
                label="Checking Dentally"
                size="small"
                sx={{ bgcolor: 'rgba(145,158,171,0.1)', color: 'text.secondary', fontWeight: 500, fontSize: 11 }}
            />
        );
    } else if (patientId) {
        patientStatusChip = (
            <Chip
                icon={<Iconify icon="mdi:check-circle" width={14} />}
                label={`Patient #${patientId}`}
                size="small"
                sx={{ bgcolor: 'rgba(0,171,85,0.1)', color: '#00AB55', fontWeight: 600, fontSize: 11 }}
            />
        );
    } else if (isExplicitlyUnlinked) {
        patientStatusChip = (
            <Chip
                icon={<Iconify icon="mdi:information-outline" width={14} />}
                label="New in Dentally"
                size="small"
                sx={{ bgcolor: 'rgba(7,141,238,0.08)', color: '#078DEE', fontWeight: 500, fontSize: 11 }}
            />
        );
    }

    return (
        <Box sx={sectionCardStyle}>
            <Typography sx={sectionLabelStyle}>
                <Iconify icon="mdi:account-outline" width={15} sx={{ color: '#078DEE' }} />
                Patient Details
            </Typography>

            {/* Read-only identity row */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '50%',
                    bgcolor: 'rgba(7,141,238,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <Iconify icon="mdi:account" width={22} sx={{ color: '#078DEE' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>{fullName}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {phone && (
                            <Typography variant="caption" color="text.secondary">{phone}</Typography>
                        )}
                        {email && (
                            <Typography variant="caption" color="text.disabled">{email}</Typography>
                        )}
                    </Stack>
                </Box>
                {patientStatusChip}
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {linkUnresolved && !patientLinkLoading && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Unable to verify the Dentally patient link. Please try again.
                </Alert>
            )}
            {profile.status === 'error' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Unable to load the current patient details. Please try again.
                </Alert>
            )}

            {/* Editable Dentally-required fields */}
            {hasMissingFields && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    The following fields are required by Dentally and will be saved to this lead.
                </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {/* Salutation — required only when a Dentally patient must be created. */}
                <TextField
                    select
                    label="Salutation"
                    size="small"
                    required={needsTitle}
                    sx={{ width: 110 }}
                    value={profileMatchesScope ? title : ''}
                    disabled={profileLoading || Boolean(persistedSalutation)}
                    onChange={(e) => { setTitle(e.target.value); setErrors(prev => ({ ...prev, title: undefined })); }}
                    error={!!errors.title}
                    helperText={errors.title || (needsTitle ? 'Required by Dentally' : '')}
                >
                    {availableSalutationOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </TextField>

                {/* Date of birth — required only when a Dentally patient must be created. */}
                {/* Keyed by scope so a practice/lead switch remounts the picker and the
                    previous DOB is never left in the committed DOM (the MUI field only
                    syncs its displayed text in a passive effect). */}
                <DateOfBirthField
                    key={profileScopeKey}
                    label="Date of birth"
                    size="small"
                    required={needsDob}
                    sx={{ width: 200 }}
                    value={profileMatchesScope ? dob : ''}
                    disabled={profileLoading || Boolean(persistedDob)}
                    onChange={(nextDob) => { setDob(nextDob); setErrors(prev => ({ ...prev, dob: undefined })); }}
                    error={!!errors.dob}
                    helperText={errors.dob || (needsDob ? 'Required by Dentally' : '')}
                />
            </Box>
        </Box>
    );
});

DentallyPatientDetailsWizardStep.displayName = 'DentallyPatientDetailsWizardStep';

export default DentallyPatientDetailsWizardStep;
